import { ServerErrorField } from "@/components/server-error-field/ServerErrorField.tsx";
import { useWorkflowFolderSave } from "@/queries/workflows/workflow-folder.query.ts";
import { ApiWorkflowFolder } from "@/types/workflows/workflow-folder.types.ts";
import { yupResolver } from "@hookform/resolvers/yup";
import { Button, Input, Modal, ModalProps, Stack, useModals } from "@skailer-com/uikit";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import * as yup from "yup";

export interface WorkflowFolderCreateModalProps extends ModalProps {
  folder?: ApiWorkflowFolder | null;
  initialTitle?: string;
  onSaved?: (folder: ApiWorkflowFolder) => void | Promise<void>;
}

export function WorkflowFolderCreateModal({
  folder,
  initialTitle,
  onSaved,
  ...props
}: WorkflowFolderCreateModalProps) {
  const { t } = useTranslation();
  const { close } = useModals();
  const { id } = props;
  const workflowFolderSchema = yup.object({
    title: yup.string().trim().required(t("errors:field_required")),
  });
  type WorkflowFolderSchema = yup.InferType<typeof workflowFolderSchema>;

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<WorkflowFolderSchema>({
    mode: "onSubmit",
    resolver: yupResolver(workflowFolderSchema),
    values: {
      title: folder?.title ?? initialTitle ?? "",
    },
  });

  const workflowFolderSave = useWorkflowFolderSave(control);

  const onSubmit = handleSubmit(async (data) => {
    try {
      const savedFolder = await workflowFolderSave.mutateAsync({
        id: folder?.id,
        payload: {
          title: data.title.trim(),
        },
      });
      await onSaved?.(savedFolder);
      close(id);
    } catch {
      return;
    }
  });

  return (
    <Modal
      {...props}
      title={folder ? t("workflow:edit_category") : t("workflow:create_category")}
      withCloser
      actions={
        <Stack gap={"sm"}>
          <ServerErrorField errors={errors} />
          <Button
            type={"submit"}
            size={"lg"}
            isLoading={workflowFolderSave.isPending}
            onClick={onSubmit}
          >
            {t("common:save")}
          </Button>
        </Stack>
      }
    >
      <form onSubmit={onSubmit}>
        <Input
          required={true}
          label={t("common:title")}
          autoFocus={true}
          type={"text"}
          {...register("title")}
          error={errors.title?.message}
        />
      </form>
    </Modal>
  );
}
